import { Injectable } from '@angular/core';
import { Router, Routes } from '@angular/router';

/**
 * DynamicRouteService - Servicio especializado para configuración de rutas dinámicas
 * Responsabilidad única: Gestionar la configuración dinámica de rutas según el rol del usuario
 */
@Injectable({
  providedIn: 'root',
})
export class DynamicRouteService {
  constructor(private router: Router) { }

  /**
   * Configura rutas dinámicas en el router principal
   * Añade las rutas proporcionadas a la ruta principal (MainComponent)
   * sin necesidad de una referencia directa al componente
   * 
   * @param dynamicRoutes Rutas a añadir dinámicamente
   */
  configureDynamicRoutes(dynamicRoutes: Routes): void {
    if (!dynamicRoutes || dynamicRoutes.length === 0) {
      return;
    }

    // Buscar la ruta principal (que tiene children y path vacío)
    const mainRoute = this.router.config.find(
      route => route.path === '' && route.children
    );

    if (mainRoute && mainRoute.children) {
      // Añadir las rutas dinámicas a las rutas existentes
      const existingRoutes = mainRoute.children;
      const newRoutes = this.mergeRoutes(existingRoutes, dynamicRoutes);
      
      mainRoute.children = newRoutes;
      
      // Resetear la configuración del router para aplicar los cambios
      this.router.resetConfig(this.router.config);
    }
  }

  /**
   * Fusiona rutas existentes con nuevas rutas, evitando duplicados
   * @param existing Rutas existentes
   * @param newRoutes Nuevas rutas a añadir
   * @returns Array de rutas fusionadas
   */
  private mergeRoutes(existing: Routes, newRoutes: Routes): Routes {
    const existingPaths = new Set(existing.map(route => route.path));
    const uniqueNewRoutes = newRoutes.filter(
      route => !existingPaths.has(route.path)
    );
    
    return [...existing, ...uniqueNewRoutes];
  }

  /**
   * Elimina todas las rutas dinámicas configuradas
   * Útil para logout o cambio de usuario
   */
  clearDynamicRoutes(): void {
    const mainRoute = this.router.config.find(
      route => route.path === '' && route.children
    );

    if (mainRoute && mainRoute.children) {
      // Mantener solo las rutas estáticas (sin datos dinámicos)
      mainRoute.children = mainRoute.children.filter(
        route => !route.data || !route.data['dynamic']
      );
      
      this.router.resetConfig(this.router.config);
    }
  }
}
